// Containment for a crashing surface.
//
// THE PROBLEM THIS SOLVES
// =======================
// One screen in forty-four has an error boundary. React's default behaviour when
// a render throws anywhere else is to unmount the entire tree — so a null
// dereference inside one Operations panel does not break that panel, it blanks
// the whole application, navigation included. The operator sees a white page and
// has no route back except the browser's reload button.
//
// TWO BOUNDARIES, AND WHY BOTH
// ============================
// `V2Routes` wraps the shell: if the chrome itself throws — YNav, the appbar, the
// EA dock, the overlay host — there is nothing left to keep, so that boundary is
// a genuine last resort and says so.
//
// `CipherShell` wraps the page: this is the one that earns its keep. A surface
// throwing leaves the Y-axis, the X-axis, the appbar and the dock alive, so the
// operator reads "this tab broke" and clicks somewhere else. That is the whole
// difference between a bug and an outage.
//
// WHY `resetKey` RATHER THAN A `key` PROP
// =======================================
// An error boundary latches: once it has caught, it renders the fallback forever
// until its state is cleared. The usual trick is `key={pathname}`, which clears
// state by remounting the subtree — but remounting throws away every surface's
// internal state on every navigation, and once offer selection starts writing to
// the URL that becomes a live regression rather than a theoretical one.
//
// So the reset is explicit: when `resetKey` changes, the latch clears and the
// children re-render in place. No remount, no discarded state.
import React from "react";

import { ErrorState } from "./states";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, lastKey: props.resetKey };
    this.retry = this.retry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  /**
   * Clear the latch when the caller says the context changed — in practice, when
   * the operator navigated somewhere else. A crashed tab must not poison the
   * next tab they visit.
   */
  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.lastKey) {
      return { error: null, lastKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error, info) {
    // Console only. There is no error-reporting service wired into this
    // application, and inventing one here would be a decision this task does not
    // own — so the trace goes where a developer will actually look for it, and
    // the gap is written up rather than filled quietly.
    // eslint-disable-next-line no-console
    console.error(
      `[cipher-v2] ${this.props.label || "surface"} crashed:`,
      error,
      info?.componentStack
    );
  }

  retry() {
    this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.retry);

    return (
      <ErrorState
        title={
          this.props.label ? `${this.props.label} could not render` : "This did not render"
        }
        detail="Something in this surface threw while rendering. The rest of the shell is unaffected — the other areas still work."
        error={error?.message}
        retry={this.retry}
      />
    );
  }
}
